import React, { useState, useEffect } from 'react';
import { Modal, Button, Alert, Spinner, Badge, Form } from 'react-bootstrap';
import { extractExcelValues, saveDataMapping, processAggregation } from '../services/api';
import 'bootstrap/dist/css/bootstrap.min.css';
import './DataMapper.css';

const DataMapper = ({ show, onHide, mappingData, onMappingComplete, onAggregationComplete, projectId }) => {  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [excelData, setExcelData] = useState([]); // Unique values from selectedColumn by runID
  const [projectParameters, setProjectParameters] = useState([]); // Selected bioreactor parameters from project
  const [mappings, setMappings] = useState({}); // Excel value -> {parameterId, aggregationMethod} mappings
  const [aggregationMethods] = useState(['Mean', 'Median', 'Sum', 'Min', 'Max', 'Count']); // Available aggregation methods
  const [processingFiles, setProcessingFiles] = useState(false);
  const [mappingSaved, setMappingSaved] = useState(false); // Track if mapping has been saved
  const [isProcessingAggregation, setIsProcessingAggregation] = useState(false); // Track aggregation processing
  useEffect(() => {
    console.log('DataMapper useEffect triggered:', { show, mappingData });
    if (show && mappingData && mappingData.project && mappingData.savedRuns) {
      initializeMapper();
    } else if (show && !mappingData) {
      console.error('DataMapper: No mapping data provided');
      setError('No mapping data provided. Please close and try again.');
      setLoading(false);
    } else if (show && mappingData && (!mappingData.project || !mappingData.savedRuns)) {
      console.error('DataMapper: Incomplete mapping data:', mappingData);
      setError('Incomplete mapping data. Missing project or savedRuns information.');
      setLoading(false);
    }
  }, [show, mappingData]);  const initializeMapper = async () => {
    try {
      console.log('DataMapper: Starting initialization...');
      console.log('DataMapper: mappingData:', mappingData);
      console.log('DataMapper: project structure:', {
        hasSelectedProcessParameters: !!mappingData.project?.selectedProcessParameters,
        hasSelectedQualityAttributes: !!mappingData.project?.selectedQualityAttributes,
        hasSelectedBioreactorData: !!mappingData.project?.selectedBioreactorData,
        hasLegacyFlags: !!(mappingData.project?.process_ph || mappingData.project?.process_temp || mappingData.project?.reactor_str)
      });
      
      setLoading(true);
      setError('');
      setProcessingFiles(true);

      // Extract project parameters (bioreactor data)
      console.log('DataMapper: Extracting project parameters...');
      const parameters = extractProjectParameters(mappingData.project);
      console.log('DataMapper: Project parameters:', parameters);
      setProjectParameters(parameters);

      // Fetch fresh run data with file information instead of using cached savedRuns
      console.log('DataMapper: Fetching fresh run data with files...');
      const { fetchBioreactorRuns } = await import('../services/api');
      const freshRuns = await fetchBioreactorRuns(mappingData.projectId);
      console.log('DataMapper: Fresh runs data:', freshRuns);

      // Process Excel files from fresh runs data
      console.log('DataMapper: Processing Excel files...');
      const excelValues = await processExcelFiles(freshRuns);
      console.log('DataMapper: Excel values returned from processExcelFiles:', excelValues);
      console.log('DataMapper: Setting excelData state with:', excelValues.length, 'items');
      console.log('DataMapper: Run IDs being set:', excelValues.map(item => item.runId));
      setExcelData(excelValues);

      console.log('DataMapper: Initialization completed successfully');
      setLoading(false);
      setProcessingFiles(false);
    } catch (err) {
      console.error('Error initializing mapper:', err);
      setError(`Failed to initialize mapper: ${err.message}`);
      setLoading(false);
      setProcessingFiles(false);
    }
  };  const extractProjectParameters = (project) => {
    const parameters = [];
    
    // FOR AGGREGATION: ONLY extract Bioreactor Data parameters
    // (Process Parameters and Quality Attributes are handled in the Excel file)
    
    // Handle new selectedBioreactorData structure (dynamic data from backend)
    if (project.selectedBioreactorData && typeof project.selectedBioreactorData === 'object') {
      console.log('DataMapper: Processing new selectedBioreactorData:', project.selectedBioreactorData);
      Object.entries(project.selectedBioreactorData).forEach(([dataName, isSelected]) => {
        if (isSelected) {
          parameters.push({
            id: dataName,
            label: dataName,
            category: 'Bioreactor Data',
            mapped: false
          });
        }
      });
    }

    // ===== LEGACY BIOREACTOR SYSTEM COMPATIBILITY =====
    // Bioreactor reactor configuration
    const reactorParams = {
      reactor_str: 'Stirred Tank Reactor',
      reactor_air: 'Air-Lift Reactor',
      reactor_packed: 'Packed Bed Reactor'
    };

    // Control parameters
    const controlParams = {
      control_temp: 'Temperature Control',
      control_ph: 'pH Control',
      control_do: 'Dissolved Oxygen Control'
    };

    // Monitoring parameters
    const monitorParams = {
      monitor_online: 'Online Monitoring',
      monitor_logging: 'Data Logging'
    };

    // Add selected reactor configuration (legacy)
    Object.entries(reactorParams).forEach(([key, label]) => {
      if (project[key]) {
        parameters.push({
          id: key,
          label: label,
          category: 'Reactor Configuration',
          mapped: false
        });
      }
    });

    // Add selected control parameters (legacy)
    Object.entries(controlParams).forEach(([key, label]) => {
      if (project[key]) {
        parameters.push({
          id: key,
          label: label,
          category: 'Control Parameters',
          mapped: false
        });
      }
    });

    // Add selected monitoring parameters (legacy)
    Object.entries(monitorParams).forEach(([key, label]) => {
      if (project[key]) {
        parameters.push({
          id: key,
          label: label,
          category: 'Monitoring',
          mapped: false
        });
      }
    });

    // Add custom bioreactor parameters (legacy)
    if (project.bioreactor_custom && project.bioreactor_custom.length > 0) {
      project.bioreactor_custom.forEach(param => {
        if (param.value) {
          parameters.push({
            id: param.id,
            label: param.name,
            category: 'Custom Bioreactor Parameters',
            mapped: false
          });
        }
      });
    }

    console.log('DataMapper: Total extracted Bioreactor Data parameters:', parameters.length);
    return parameters;
  };const processExcelFiles = async (runs) => {
    try {
      console.log('DataMapper: processExcelFiles called with runs:', runs);
      console.log('DataMapper: Using projectId:', mappingData.projectId);
      
      // Call the backend API to extract unique values from Excel files
      const projectId = mappingData.projectId;
      const response = await extractExcelValues(projectId, runs);
      
      console.log('DataMapper: API response:', response);
      console.log('DataMapper: Response success:', response.success);
      console.log('DataMapper: Response data:', response.data);
      
      if (response.success && response.data) {
        // Check if we have headers (available columns) from the Excel files
        if (response.data.headers && response.data.headers.length > 0) {
          console.log('DataMapper: Found Excel headers:', response.data.headers);
          console.log('DataMapper: Data preview:', response.data.data_preview);
          
          // Create structure for column selection interface
          const excelValues = [];
          const processedRuns = new Set(); // Track processed runs to avoid duplicates
          
          console.log('DataMapper: Processing runs:', runs);
          console.log('DataMapper: Total runs to process:', runs.length);
          console.log('DataMapper: Run IDs to process:', runs.map(r => r.runId));
          
          for (const run of runs) {
            console.log('DataMapper: Processing run:', run.runId, run);
            
            // Skip if we've already processed this run
            if (processedRuns.has(run.runId)) {
              console.log('DataMapper: Skipping duplicate run:', run.runId);
              continue;
            }
            
            // Handle both 'bioreactorFiles' and 'files' field names
            const filesData = run.bioreactorFiles || run.files || [];
            console.log('DataMapper: Files data for run:', run.runId, filesData);
            
            if (filesData.length > 0) {
              // Process only the first Excel file for each run to avoid duplicates
              const file = filesData[0]; // Take first file only
              console.log('DataMapper: Processing first file:', file);
              
              // Get file name from different possible field names
              const fileName = file.name || file.filename || file.fileName || 'Unknown File';
              const filePath = file.filePath || file.file_path || file.path;
              console.log('DataMapper: File details - fileName:', fileName, 'filePath:', filePath);
              
              if (filePath) {
                const newItem = {
                  runId: run.runId,
                  fileName: fileName,
                  filePath: filePath,
                  availableColumns: response.data.headers, // Available columns for selection
                  selectedColumn: file.selectedColumn || null, // Currently selected column (if any)
                  uniqueValues: file.selectedColumn && response.data.unique_values[file.selectedColumn] 
                               ? response.data.unique_values[file.selectedColumn] 
                               : [], // Unique values if column is selected
                  dataPreview: response.data.data_preview || [], // Preview of the data
                  mapped: false
                };
                console.log('DataMapper: Adding excel item:', newItem);
                excelValues.push(newItem);
                processedRuns.add(run.runId); // Mark this run as processed
              } else {
                console.log('DataMapper: Skipping file - no filePath found:', file);
              }
            } else {
              console.log('DataMapper: Run has no files:', run.runId);
            }
          }
          
          console.log('DataMapper: Processed excel values:', excelValues);
          console.log('DataMapper: Total processed runs:', excelValues.length);
          console.log('DataMapper: Run IDs processed:', excelValues.map(item => item.runId));
          return excelValues;
        } else {
          console.log('DataMapper: No headers found in response');
          return [];
        }
      } else {
        throw new Error('Failed to extract Excel values from backend');
      }
    } catch (error) {
      console.error('Error calling backend API:', error);
      
      // Fallback to simulated data if API fails
      console.log('Using fallback data extraction...');
      const excelValues = [];

      for (const run of runs) {
        if (run.bioreactorFiles && run.bioreactorFiles.length > 0) {
          for (const file of run.bioreactorFiles) {
            if (file.selectedColumn && file.filePath) {
              try {
                const uniqueValues = await extractUniqueValuesFromFile(file, run.runId);
                if (uniqueValues.length > 0) {
                  excelValues.push({
                    runId: run.runId,
                    fileName: file.name,
                    selectedColumn: file.selectedColumn,
                    uniqueValues: uniqueValues,
                    mapped: false
                  });
                }
              } catch (err) {
                console.error(`Error processing file ${file.name}:`, err);
                // Continue with other files
              }
            }
          }
        }
      }

      return excelValues;
    }
  };

  const extractUniqueValuesFromFile = async (file, runId) => {
    // This is a placeholder implementation
    // In a real application, you would call a backend API to read the file and extract unique values
    // from the selectedColumn. For now, we'll return some sample data based on the CSV examples.
    
    // Sample data based on the CSV files we saw in the search results
    const sampleValues = [
      'R01pHTransmitter.PV',
      'R01MFC02Transmitter.PV', 
      'R01DissolvedOxygenTransmitter.PV',
      'R01MFC01Transmitter.PV',
      'R01Pump01Transmitter.PV'
    ];

    // Filter to simulate unique values from the selected column
    const selectedColumnValues = sampleValues.filter(value => 
      value.toLowerCase().includes(file.selectedColumn.toLowerCase()) ||
      file.selectedColumn.toLowerCase().includes(value.toLowerCase().split('.')[0].toLowerCase())
    );

    return selectedColumnValues.length > 0 ? selectedColumnValues : sampleValues.slice(0, 3);
  };  const handleDirectMapping = (excelValue, parameterId) => {
    const parameter = projectParameters.find(p => p.id === parameterId);
    if (parameter) {
      setMappings(prev => ({
        ...prev,
        [excelValue]: {
          parameterId: parameterId,
          aggregationMethod: 'Mean' // Default aggregation method
        }
      }));

      // Mark parameter as mapped
      setProjectParameters(prev => 
        prev.map(param => 
          param.id === parameterId ? { ...param, mapped: true } : param
        )
      );
    }
  };

  const handleColumnSelection = async (runId, fileName, selectedColumn) => {
    console.log('DataMapper: Column selected:', { runId, fileName, selectedColumn });
    
    try {
      setProcessingFiles(true);
      
      // Update the excelData to mark this file as having a selected column
      setExcelData(prev => prev.map(item => {
        if (item.runId === runId && item.fileName === fileName) {
          return {
            ...item,
            selectedColumn: selectedColumn
          };
        }
        return item;
      }));
      
      // HARDCODED TEST - Create the exact data structure that works
      const testData = {
        savedRuns: [
          {
            runId: runId,
            bioreactorFiles: [
              {
                name: fileName,
                filePath: runId === 'RUN-001' ? '76d60460-ece9-41c0-be73-057b2d733251.xlsx' : '8a12fb3b-34c7-4d8a-8362-ae8faec63760.xlsx',
                selectedColumn: selectedColumn
              }
            ]
          }
        ]
      };
      
      console.log('DataMapper: HARDCODED TEST - Sending data:', JSON.stringify(testData, null, 2));
      
      // Get unique values from backend using hardcoded data
      const response = await extractExcelValues(mappingData.projectId, testData.savedRuns);
      
      console.log('=== API CALL DEBUG ===');
      console.log('DataMapper: API URL:', `${mappingData.projectId}/extract-excel-values`);
      console.log('DataMapper: Request payload:', JSON.stringify(testData.savedRuns, null, 2));
      console.log('DataMapper: Backend response:', JSON.stringify(response, null, 2));
      console.log('DataMapper: Selected column:', selectedColumn);
      console.log('====================');
      
      if (response && response.success && response.data && response.data.unique_values) {
        console.log('DataMapper: All unique values:', response.data.unique_values);
        console.log('DataMapper: Unique values for selected column:', response.data.unique_values[selectedColumn]);
        
        const columnUniqueValues = response.data.unique_values[selectedColumn] || [];
        console.log('DataMapper: Final unique values to set:', columnUniqueValues);
        
        // Update the excelData with the unique values
        setExcelData(prev => {
          const updated = prev.map(item => {
            if (item.runId === runId && item.fileName === fileName) {
              return {
                ...item,
                uniqueValues: columnUniqueValues
              };
            }
            return item;
          });
          console.log('DataMapper: Updated excelData:', updated);
          return updated;
        });
      } else {
        console.error('DataMapper: Failed to get unique values:', response);
        setError('Failed to extract unique values from selected column. Response: ' + JSON.stringify(response));
      }
      
    } catch (error) {
      console.error('Error getting unique values for column:', error);
      setError('Failed to get unique values for selected column. Please try again.');
    } finally {
      setProcessingFiles(false);
    }
  };

  const updateAggregationMethod = (excelValue, aggregationMethod) => {
    setMappings(prev => ({
      ...prev,
      [excelValue]: {
        ...prev[excelValue],
        aggregationMethod: aggregationMethod
      }
    }));
  };
  const removeMapping = (excelValue) => {
    const mapping = mappings[excelValue];
    const parameterId = mapping ? mapping.parameterId : null;
    
    setMappings(prev => {
      const newMappings = { ...prev };
      delete newMappings[excelValue];
      return newMappings;
    });

    // Unmark parameter
    if (parameterId) {
      setProjectParameters(prev => 
        prev.map(param => 
          param.id === parameterId ? { ...param, mapped: false } : param
        )
      );
    }
  };
  const handleSaveMapping = async () => {
    try {
      const mappingDataToSave = {
        mappings,
        excelValues: excelData.map(item => ({
          runId: item.runId,
          fileName: item.fileName,
          selectedColumn: item.selectedColumn,
          uniqueValues: item.uniqueValues
        })),
        projectParameters: projectParameters.map(param => ({
          id: param.id,
          label: param.label,
          category: param.category,
          mapped: param.mapped
        }))
      };      console.log('Saving mapping data to database:', mappingDataToSave);      // Save to database
      setLoading(true);
      const currentProjectId = projectId || mappingData?.projectId;
      console.log('Saving mapping for project:', currentProjectId);
      
      if (!currentProjectId) {
        throw new Error('Project ID is not available. Please close the mapper and try again.');
      }
      
      const result = await saveDataMapping(currentProjectId, mappingDataToSave);
        console.log('Mapping saved successfully:', result);
      
      // Mark mapping as saved
      setMappingSaved(true);
      
      // Call parent completion handler
      if (onMappingComplete) {        onMappingComplete({
          ...mappingDataToSave,
          mappingId: result.mappingId,
          totalMappings: Object.keys(mappings).length
        });
      }
      
      // Don't close modal to allow aggregation
      // onHide();
    } catch (error) {
      console.error('Error saving mapping:', error);
      setError(`Failed to save mapping: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  const handleProceedToAggregate = async () => {
    try {
      setIsProcessingAggregation(true);
      setError('');
      
      // Use projectId prop directly instead of mappingData.projectId
      const currentProjectId = projectId || mappingData?.projectId;
      console.log('Starting aggregation process for project:', currentProjectId);
      console.log('Available projectId sources:', { projectIdProp: projectId, mappingDataProjectId: mappingData?.projectId });
      
      if (!currentProjectId) {
        throw new Error('Project ID is not available. Please close the mapper and try again.');
      }
        const result = await processAggregation(currentProjectId);
      
      console.log('DataMapper: Aggregation completed:', result);
      
      // Validate the result structure
      if (!result) {
        throw new Error('Aggregation returned null or undefined result');
      }
      
      if (!result.hasOwnProperty('processedDates')) {
        console.warn('DataMapper: Result missing processedDates property:', result);
      }
      
      if (!result.hasOwnProperty('aggregationId')) {
        console.warn('DataMapper: Result missing aggregationId property:', result);
      }
      
      // Show success message with safe property access
      const processedCount = result?.processedDates?.length || 0;
      const aggregationId = result?.aggregationId || 'Unknown';
      
      alert(`Aggregation completed successfully!\n\nProcessed ${processedCount} date ranges.\nAggregation ID: ${aggregationId}\n\nUpdated Excel file is ready for viewing.`);
      
      // Call parent aggregation completion handler
      if (onAggregationComplete) {
        try {
          onAggregationComplete(result);
        } catch (error) {
          console.error('DataMapper: Error in aggregation completion callback:', error);
          throw new Error(`Callback error: ${error.message}`);
        }
      }
      
      onHide();
    } catch (error) {
      console.error('Error processing aggregation:', error);
      
      // Better error message handling
      let errorMessage = 'Unknown error occurred';
      if (error?.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error?.detail) {
        errorMessage = error.detail;
      } else {
        errorMessage = 'Failed to process aggregation';
      }
      
      setError(`Failed to process aggregation: ${errorMessage}`);
    } finally {
      setIsProcessingAggregation(false);
    }
  };

  const groupByCategory = (parameters) => {
    return parameters.reduce((groups, param) => {
      const category = param.category;
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(param);
      return groups;
    }, {});
  };

  if (loading) {
    return (
      <Modal show={show} onHide={onHide} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Data Mapper - Loading</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center p-5">
          <Spinner animation="border" variant="primary" />
          <div className="mt-3">
            {processingFiles ? 'Processing Excel files...' : 'Initializing mapper...'}
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  const groupedParameters = groupByCategory(projectParameters);  return (
    <Modal show={show} onHide={onHide} size="xl" centered className="data-mapper">
      <Modal.Header closeButton>
        <Modal.Title>🔗 Data Mapper</Modal.Title>
      </Modal.Header>
      <Modal.Body>{error && (
          <Alert variant="danger" className="mb-3">
            <strong>Error:</strong> {error}
          </Alert>
        )}
        
        {mappingSaved && (
          <Alert variant="success" className="mb-3">
            <strong>Mapping Saved Successfully!</strong> You can now proceed with aggregation to process your bioreactor data and update the Excel file with aggregated values.
          </Alert>
        )}
        
        <div className="mapper-instructions mb-4">
          <Alert variant="info">
            <strong>Instructions:</strong> Map the unique values from your Excel files (left) to the bioreactor parameters 
            selected in your project configuration (right). Use the dropdown selectors to create mappings.
          </Alert>
        </div>

        <div className="row">
          {/* Left Panel - Excel Values */}
          <div className="col-md-6">
            <div className="card h-100">
              <div className="card-header bg-primary text-white">
                <h5 className="mb-0">📊 Excel Data Values</h5>
              </div>
              <div className="card-body">
                {console.log('DataMapper: Rendering Excel Data Values section, excelData:', excelData)}
                {console.log('DataMapper: excelData length:', excelData.length)}
                {console.log('DataMapper: excelData run IDs:', excelData.map(item => item.runId))}
                {excelData.length === 0 ? (
                  <div className="empty-state">
                    <i className="fas fa-file-excel mb-3" style={{ fontSize: '3rem', color: '#3b82f6' }}></i>
                    <div className="fw-medium mb-2">No Excel data found</div>
                    <small className="text-muted">Make sure you have uploaded Excel files in your project runs</small>
                  </div>
                ) : (
                  excelData
                    .map((runData, runIndex) => (
                    <div key={runData.runId} className="excel-run-section">
                      <div className="excel-run-header">
                        <div className="excel-run-title">{runData.runId} - {runData.fileName}</div>
                        {runData.selectedColumn ? (
                          <Badge className="excel-run-status mapped">{runData.selectedColumn}</Badge>
                        ) : (
                          <Badge className="excel-run-status unmapped">No column selected</Badge>
                        )}
                      </div>
                      
                      {/* Show available columns as cards for mapping */}
                      {runData.availableColumns && runData.availableColumns.length > 0 && (
                        <div className="available-columns-section">
                          <div className="available-columns-header">
                            <i className="fas fa-table me-2 text-primary"></i>
                            <span className="fw-semibold">Available Columns</span>
                          </div>
                          
                          {runData.availableColumns
                            .filter(column => 
                              !column.toLowerCase().includes('date') && 
                              !column.toLowerCase().includes('time') &&
                              !column.toLowerCase().includes('datetime') &&
                              !column.toLowerCase().includes('timestamp')
                            )
                            .map((column, idx) => (
                              <div key={idx} className="column-card">
                                <div className="column-card-header">
                                  <div className="column-card-title">
                                    📊 {column}
                                  </div>
                                  {Object.hasOwnProperty.call(mappings, column) ? (
                                    <Badge className="excel-run-status mapped">Mapped</Badge>
                                  ) : (
                                    <Badge className="excel-run-status unmapped">Not Mapped</Badge>
                                  )}
                                </div>
                                
                                {/* Check if this column is already mapped */}
                                {Object.hasOwnProperty.call(mappings, column) ? (
                                  <div className="column-mapped-state">
                                    <div className="d-flex align-items-center justify-content-between">
                                      <div className="text-success fw-medium">
                                        <i className="fas fa-arrow-right me-2"></i>
                                        Mapped to: {projectParameters.find(p => p.id === mappings[column]?.parameterId)?.label || 'Unknown Parameter'}
                                      </div>
                                      <Button 
                                        size="sm" 
                                        variant="outline-danger"
                                        onClick={() => removeMapping(column)}
                                      >
                                        Remove
                                      </Button>
                                    </div>
                                    
                                    {/* Aggregation method for mapped column */}
                                    <div className="aggregation-section">
                                      <div className="d-flex align-items-center">
                                        <Form.Label className="small me-2 mb-0 fw-medium">Aggregation:</Form.Label>
                                        <Form.Select
                                          size="sm"
                                          className="aggregation-select"
                                          style={{ width: 'auto' }}
                                          value={mappings[column]?.aggregationMethod || 'Mean'}
                                          onChange={(e) => updateAggregationMethod(column, e.target.value)}
                                        >
                                          {aggregationMethods.map(method => (
                                            <option key={method} value={method}>{method}</option>
                                          ))}
                                        </Form.Select>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  /* Mapping dropdown for unmapped column - inline within card */
                                  <div className="column-mapping-controls">
                                    <Form.Select 
                                      size="sm" 
                                      className="form-select"
                                      onChange={(e) => e.target.value && handleDirectMapping(column, e.target.value)}
                                      value=""
                                    >
                                      <option value="">Select parameter to map to...</option>
                                      {projectParameters.filter(p => !p.mapped).map(param => (
                                        <option key={param.id} value={param.id}>
                                          {param.label} ({param.category})
                                        </option>
                                      ))}
                                    </Form.Select>
                                  </div>
                                )}
                              </div>
                            ))
                          }
                        </div>
                      )}
                      
                      {/* Show column mapping interface if column is selected */}
                      {runData.selectedColumn && runData.uniqueValues && runData.uniqueValues.length > 0 && (
                        <div className="column-mapping-card">
                          <div className="column-mapping-header">
                            <div className="column-mapping-title">
                              📊 Column: {runData.selectedColumn}
                            </div>
                          </div>
                          
                          <div className="column-mapping-stats">
                            {runData.uniqueValues.length} unique values found (e.g., {runData.uniqueValues.slice(0, 3).join(', ')}{runData.uniqueValues.length > 3 ? '...' : ''})
                          </div>
                            
                          {/* Check if this column is already mapped */}
                          {Object.hasOwnProperty.call(mappings, runData.selectedColumn) ? (
                            <div className="column-mapped-state">
                              <div className="d-flex align-items-center justify-content-between">
                                <div className="text-success fw-medium">
                                  <i className="fas fa-arrow-right me-2"></i>
                                  Mapped to: {projectParameters.find(p => p.id === mappings[runData.selectedColumn]?.parameterId)?.label || 'Unknown Parameter'}
                                </div>
                                <Button 
                                  size="sm" 
                                  variant="outline-danger"
                                  onClick={() => removeMapping(runData.selectedColumn)}
                                >
                                  Remove Mapping
                                </Button>
                              </div>
                              
                              {/* Aggregation method for mapped column */}
                              <div className="aggregation-section">
                                <div className="d-flex align-items-center">
                                  <Form.Label className="small me-2 mb-0 fw-medium">Aggregation Method:</Form.Label>
                                  <Form.Select
                                    size="sm"
                                    className="aggregation-select"
                                    style={{ width: 'auto' }}
                                    value={mappings[runData.selectedColumn]?.aggregationMethod || 'Mean'}
                                    onChange={(e) => updateAggregationMethod(runData.selectedColumn, e.target.value)}
                                  >
                                    {aggregationMethods.map(method => (
                                      <option key={method} value={method}>{method}</option>
                                    ))}
                                  </Form.Select>
                                </div>
                                <div className="small text-muted mt-1">
                                  How should multiple values in this column be combined?
                                </div>
                              </div>
                            </div>
                          ) : (
                            /* Mapping dropdown for unmapped column */
                            <div className="mt-3">
                              <Form.Select 
                                size="sm" 
                                className="form-select"
                                onChange={(e) => e.target.value && handleDirectMapping(runData.selectedColumn, e.target.value)}
                                value=""
                              >
                                <option value="">Select parameter to map to...</option>
                                {projectParameters.filter(p => !p.mapped).map(param => (
                                  <option key={param.id} value={param.id}>
                                    {param.label} ({param.category})
                                  </option>
                                ))}
                              </Form.Select>
                              <div className="small text-muted mt-2">
                                Map the entire "{runData.selectedColumn}" column to a bioreactor parameter
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Right Panel - Project Parameters */}
          <div className="col-md-6">
            <div className="card h-100">
              <div className="card-header bg-success text-white">
                <h5 className="mb-0">⚙️ Project Bioreactor Parameters</h5>
                <small>Selected parameters from your project configuration</small>
              </div>
              <div className="card-body">                {Object.keys(groupedParameters).length === 0 ? (
                  <div className="empty-state">
                    <i className="fas fa-cogs mb-3" style={{ fontSize: '3rem', color: '#10b981' }}></i>
                    <div className="fw-medium mb-2">No bioreactor parameters selected</div>
                    <small className="text-muted">Please configure bioreactor parameters in your project settings</small>
                  </div>) : (
                  Object.entries(groupedParameters).map(([category, params]) => (
                    <div key={category} className="mb-4">
                      <div className="parameter-category-header">
                        {category}
                      </div>
                      {params.map((param, index) => (
                        <div
                          key={param.id}
                          className={`parameter-item ${
                            param.mapped ? 'border-primary bg-primary bg-opacity-10' : ''
                          }`}
                        >
                          <div className="d-flex justify-content-between align-items-center w-100">
                            <span className="flex-grow-1 fw-medium">{param.label}</span>
                            {param.mapped && <Badge bg="primary">Mapped</Badge>}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Mapping Summary */}
        <div className="row mt-4">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h6 className="mb-0">📋 Mapping Summary</h6>
              </div>
              <div className="card-body">
                {Object.keys(mappings).length === 0 ? (
                  <div className="empty-state">
                    <i className="fas fa-link mb-3"></i>
                    <div className="text-muted">No mappings created yet</div>
                    <small>Start by selecting values from the left panel to map to parameters</small>
                  </div>
                ) : (
                  <div>
                    <div className="total-mappings-header">
                      <div className="total-mappings-content">
                        <div className="total-mappings-count">
                          <strong>Total Mappings:</strong> 
                          <Badge bg="primary">{Object.keys(mappings).length}</Badge>
                        </div>
                        <div className="total-mappings-status">
                          Ready for aggregation processing
                        </div>
                      </div>
                    </div>                    <div className="mapping-list">
                      {Object.entries(mappings).map(([excelValue, mapping]) => {
                        const parameter = projectParameters.find(p => p.id === mapping.parameterId);
                        return (
                          <div key={excelValue} className="mapping-summary-item">
                            <div className="d-flex justify-content-between align-items-center">
                              <div className="flex-grow-1">
                                <div className="d-flex align-items-center mb-2">
                                  <div className="excel-value-text me-2" style={{ maxWidth: '200px' }}>
                                    {excelValue}
                                  </div>
                                  <i className="fas fa-arrow-right mapping-arrow"></i>
                                  <span className="text-primary fw-medium">
                                    {parameter?.label || 'Unknown Parameter'}
                                  </span>
                                </div>
                                <div className="d-flex align-items-center">
                                  <span className="aggregation-badge">
                                    {mapping.aggregationMethod}
                                  </span>
                                  <span className="small text-muted ms-2">
                                    Aggregation method for data processing
                                  </span>
                                </div>
                              </div>
                              <Button 
                                size="sm" 
                                variant="outline-danger"
                                onClick={() => removeMapping(excelValue)}
                                className="ms-3"
                              >
                                Remove
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
        <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        {!mappingSaved ? (
          <Button 
            variant="primary" 
            onClick={handleSaveMapping}
            disabled={Object.keys(mappings).length === 0 || loading}
          >
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              `Save Mapping (${Object.keys(mappings).length})`
            )}
          </Button>
        ) : (
          <Button 
            variant="success" 
            onClick={handleProceedToAggregate}
            disabled={isProcessingAggregation}
          >
            {isProcessingAggregation ? (
              <>
                <Spinner size="sm" className="me-2" />
                Processing Aggregation...
              </>
            ) : (
              'Proceed to Aggregate'
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default DataMapper;
